<template>
    <DialogBase class="dialog-camera">

        <template #title>
            Take&nbsp;Photo
        </template>

        <template #content>

            <v-select 
                v-if="hasDevice" 
                hide-details 
                label="select camera" 
                v-model="activeDevice" 
                :items="deviceItems">
            </v-select>

            <div class="camera-preview">
                <video autoplay muted playsinline ref="video"></video>
            </div>

            <canvas ref="canvas" style="display:none;"></canvas>

        </template>

        <template #actions>
            <v-btn 
                variant="elevated" 
                elevation="2" 
                color="surface" 
                @click="handleCancel">
                Cancel
            </v-btn>
            <v-btn 
                variant="elevated" 
                elevation="2" 
                color="color1" 
                @click="handlePhoto">
                Capture
            </v-btn>
        </template>

    </DialogBase>
</template>

<script>
import LEVELS from '@generio-ai/library-legacy/constants/levels'
import { mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import { DialogBase } from '@generio-ai/library-legacy/components/dialogs'

export default {
    name: 'DialogCamera',
    components: {
        DialogBase
    },
    props: {
        callback: {
            type: Function,
            default: null
        }
    },
    data() {
        return {
            stream: null,
            activeStream: false,
            mirrorStream: false,
            devices: null,
            activeDevice: null
        }
    },
    computed: {
        hasDevice() {
            if (this.devices == null || this.devices.length <= 0) return false
            return true
        },
        hasMultipleDevices() {
            if (!this.hasDevice) return false
            if (this.devices.length <= 1) return false
            return true
        },
        deviceItems() {
            return this.devices.map(device => device.label)
        },
        device() {
            if (!this.hasDevice) return null
            if (this.activeDevice == null) return null
            return this.devices.find(device => device.label === this.activeDevice)
        }
    },
    watch: {
        activeDevice(newVal, oldVal) {
            if (newVal === oldVal) return
            this.switchCamera()
        }
    },
    methods:
    {
        ...mapActions('analytics', [
            'logEvent'
        ]),
        ...mapActions('dialogs', [
            'close'
        ]),
        ...mapActions('notifications', [
            'show',
        ]),
        handleCancel()
        {
            this.close()
        },
        async initDevices() {
            try {
                const allDevices = await navigator.mediaDevices.enumerateDevices()
                this.devices = allDevices.filter(
                    device => device.kind === 'videoinput')
                if (this.devices.length > 0) {
                    this.activeDevice = this.devices[0].label
                    this.startCamera()
                }
            }
            catch (error) {
                this.show({
                    message: 'Unable to retrieve list of camera devices.',
                    level: LEVELS.ERROR
                })
            }
        },
        async updateStream() {
            this.$nextTick(() => {

                // if stream is not active, then start the camera stream
                if (!this.activeStream)
                    this.startCamera()

                // if stream is active, then stop the camera stream
                else if (this.activeStream)
                    this.stopCamera()
            })
        },
        handlePhoto() {
            // abort if stream is not active
            if (!this.activeStream) return

            // capture the current video frame into the canvas
            const video = this.$refs.video
            const canvas = this.$refs.canvas
            const context = canvas.getContext('2d')

            // set canvas size to match video size
            canvas.width = video.videoWidth
            canvas.height = video.videoHeight

            // mirror the canvas if needed
            if (this.mirrorStream) {
                context.save()
                context.scale(-1, 1)
                context.translate(-canvas.width, 0)
            }

            // draw the video frame on the canvas
            context.drawImage(video, 0, 0, canvas.width, canvas.height)

            // if mirrored, restore the context to its original state
            if (this.mirrorStream) {
                context.restore()
            }

            // get the base64 image from the canvas
            const base64 = canvas.toDataURL('image/png')

            // trigger callback
            if (this.callback && typeof this.callback === 'function') {
                this.$nextTick(() => this.callback(base64))
            }

            // close dialog
            this.close()

            this.logEvent({
                name: "Camera Photo",
                description: "User took a photo with a camera"
            })
        },
        switchCamera() {
            // restart the camera with the new device
            if (this.activeStream) {
                this.stopCamera()
                this.startCamera()
            }

            this.logEvent({
                name: "Camera Source",
                description: "User selected a different camera source"
            })
        },
        async startCamera() {
            // get current device id
            const deviceId = this.device.deviceId

            try {
                // set stream to active
                this.activeStream = true

                // get stream
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: { deviceId }
                })
                if (this.activeStream) {
                    this.$refs.video.srcObject = stream
                    this.stream = stream

                    // detect if the current camera is front-facing (then mirror video)
                    const videoTrack = stream.getVideoTracks()[0]
                    this.mirrorStream = videoTrack.label.toLowerCase().includes('front')

                    // mirror the video if necessary
                    if (this.mirrorStream) {
                        this.$refs.video.style.transform = 'scaleX(-1)'
                    } else {
                        this.$refs.video.style.transform = 'none'
                    }
                }
            }
            catch (error) {
                // deactivate stream
                this.activeStream = false
                console.log(error)

                this.show({
                    message: 'Unable to access the camera.',
                    level: LEVELS.ERROR
                })
            }
        },
        stopCamera() {
            if (this.activeStream) {
                // deactivate stream
                this.activeStream = false

                if (this.stream) {
                    // stop the video tracks
                    const tracks = this.stream.getTracks()
                    tracks.forEach(track => track.stop())

                    // reset the stream
                    this.stream = null
                }
            }
        }
    },
    mounted() {
        // initialize the camera devices
        this.initDevices()
    },
    beforeDestroy() {
        // stop the camera when the component is destroyed
        this.stopCamera()
    }
}
</script>

<style scoped>
.camera-preview {
    display: flex;
    width: 100%;
    padding: 10px;
    justify-content: center;
    align-items: center;
}

video {
    width: auto;
    height: 100%;
    max-height: 50vh;
    background-color: rgb(var(--v-theme-background));
    margin-top: 0px;
}
</style>